import { useCallback, useEffect, useRef } from "react"

import { useModal } from "@finanedi.es/front/common/hooks"
import {
  useGetUsuarioByCurrentUser,
  useUsuarioEmpresaActiva,
} from "api/Usuario.hook"
import { getValidateModalStatusSelector } from "features/User/redux/selectors/userSelectors"
import { useSelector } from "hooks/useSelector"
import { useHistory } from "react-router-dom"

import {
  configuracionRequeridaDefinitivoMessage,
  configuracionRequeridaMessage,
  esPostFechaLimite,
} from "../utils"

const storageKeyShownNormal = "leyAntifraudeModalShown"
const storageKeyCancelledNormal = "leyAntifraudeModalCancelled"
const storageKeyShownDefinitivo = "leyAntifraudeModalShownDefinitivo"
const empresaChangingKey = "empresaActivaChanging"

export const getIsChangingEmpresa = (): boolean => {
  return sessionStorage.getItem(empresaChangingKey) === "true"
}

export const useLeyAntifraudeConfiguracionRequerida = (
  configurarLeyAntifraudeDefinitivo = false
) => {
  const history = useHistory()
  const { modals, openModal, closeModal } = useModal()
  const hasOpenedRef = useRef(false)

  const { showValidarEmpresaModal } = useSelector(
    getValidateModalStatusSelector
  )

  const {
    usuario: { verificacionEmpresa, empresaActiva },
  } = useGetUsuarioByCurrentUser()

  const { activeCompanyIsFetching } = useUsuarioEmpresaActiva()

  const empresaId = empresaActiva?.id
  const configuracionAntifraude =
    empresaActiva?.configuracion?.configuracionAntifraude

  const storageKeyShown = configurarLeyAntifraudeDefinitivo
    ? storageKeyShownDefinitivo
    : storageKeyShownNormal
  const storageKeyCancelled = storageKeyCancelledNormal

  const handleConfirm = useCallback(() => {
    hasOpenedRef.current = false
    if (empresaId) {
      sessionStorage.setItem(storageKeyShown, empresaId)
    }
    closeModal("confirmDialog")

    if (configurarLeyAntifraudeDefinitivo) {
      history.push("/perfil/empresa?openLeyAntifraude=true")
    } else {
      openModal("configModal")
    }
  }, [
    openModal,
    closeModal,
    empresaId,
    storageKeyShown,
    configurarLeyAntifraudeDefinitivo,
    history,
  ])

  const handleCancel = useCallback(() => {
    hasOpenedRef.current = false
    if (empresaId) {
      sessionStorage.setItem(storageKeyCancelled, empresaId)
    }
    closeModal("confirmDialog")
  }, [empresaId, closeModal, storageKeyCancelled])

  const shownForEmpresaId = sessionStorage.getItem(storageKeyShown)
  const cancelledForEmpresaId = sessionStorage.getItem(storageKeyCancelled)
  const alreadyShownForThisEmpresa = shownForEmpresaId === empresaId
  const alreadyCancelledForThisEmpresa = cancelledForEmpresaId === empresaId
  const isChangingEmpresa =
    sessionStorage.getItem(empresaChangingKey) === "true"

  const cumpleFechaLimite = configurarLeyAntifraudeDefinitivo
    ? esPostFechaLimite()
    : true

  const shouldShow = configurarLeyAntifraudeDefinitivo
    ? Boolean(empresaId) &&
      cumpleFechaLimite &&
      !isChangingEmpresa &&
      !activeCompanyIsFetching &&
      verificacionEmpresa?.verificado &&
      !showValidarEmpresaModal &&
      empresaActiva?.configuracion?.configuracionAntifraude === null
    : Boolean(empresaId) &&
      cumpleFechaLimite &&
      !isChangingEmpresa &&
      !activeCompanyIsFetching &&
      verificacionEmpresa?.verificado &&
      !showValidarEmpresaModal &&
      !alreadyShownForThisEmpresa &&
      !alreadyCancelledForThisEmpresa &&
      empresaActiva?.configuracion?.configuracionAntifraude === null

  const message = configurarLeyAntifraudeDefinitivo
    ? configuracionRequeridaDefinitivoMessage
    : configuracionRequeridaMessage

  useEffect(() => {
    if (!shouldShow || hasOpenedRef.current || !empresaId) return

    const isCurrentlyChangingEmpresa =
      sessionStorage.getItem(empresaChangingKey) === "true"

    if (configurarLeyAntifraudeDefinitivo) {
      if (!isCurrentlyChangingEmpresa) {
        hasOpenedRef.current = true
        sessionStorage.setItem(storageKeyShown, empresaId)
        openModal("confirmDialog")
      }
    } else {
      const currentShownForEmpresaId = sessionStorage.getItem(storageKeyShown)
      const currentCancelledForEmpresaId =
        sessionStorage.getItem(storageKeyCancelled)

      const alreadyHandled =
        currentShownForEmpresaId === empresaId ||
        currentCancelledForEmpresaId === empresaId

      if (!isCurrentlyChangingEmpresa && !alreadyHandled) {
        hasOpenedRef.current = true
        sessionStorage.setItem(storageKeyShown, empresaId)
        openModal("confirmDialog")
      }
    }
  }, [
    shouldShow,
    openModal,
    empresaId,
    storageKeyShown,
    storageKeyCancelled,
    configurarLeyAntifraudeDefinitivo,
  ])

  useEffect(() => {
    if (configurarLeyAntifraudeDefinitivo && configuracionAntifraude !== null) {
      hasOpenedRef.current = false
    }
  }, [configuracionAntifraude, configurarLeyAntifraudeDefinitivo])

  return {
    isConfirmDialogOpen: modals.confirmDialog,
    handleConfirm,
    handleCancel,
    isConfigModalOpen: modals.configModal,
    handleCloseConfigModal: () => closeModal("configModal"),
    nif: empresaActiva?.nif ?? "",
    razonSocial: empresaActiva?.razonSocial ?? "",
    message,
  }
}
